import { Box, Stack, Text } from '@atlaskit/primitives';
import { useDialog } from '@databases/dialog';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import usePanelState from '@databases/panel/usePanelState';
import { defaultStoreUtils, type DefaultRootState } from '@databases/redux';
import {
  isFieldSelected,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { doDeleteFields } from '@databases/redux/contextual-menu/actions';
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';
import { getService } from '@databases/service-container';
import { DatabaseFieldType } from '@databases/types/database';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { DEPENDENCY_FIELD_TYPES } from '../constants';

type UseSharedActionsMethodsProps = {
  contentId: string;
  state: DefaultRootState;
};

export const useSharedActionsMethods = ({
  contentId,
  state,
}: UseSharedActionsMethodsProps) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const { showDialog } = useDialog();
  const { hidePanel } = usePanel();
  const { getData } = usePanelState();
  const { fieldId: panelFieldId } = getData('FieldSettingsPanel') || {};

  const handleDeleteFields = useCallback(() => {
    const fieldNames: string[] = [];
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const selectedFieldIds = selectSelectedFieldIds(state, {
      contentId,
    });

    selectedFieldIds.forEach((fieldId) => {
      if (database.fieldsById[fieldId]) {
        fieldNames.push(database.fieldsById[fieldId].name);
      }
    });

    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            <FormattedMessage
              {...messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ]}
              values={{
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>,
              }}
            />
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        selectionsActions.clearSelectionIntent();

        if (result) {
          if (isFieldSelected(state, contentId, panelFieldId)) {
            hidePanel();
          }

          dispatch(doDeleteFields(contentId));
        }
      },
    });
  }, [
    contentId,
    dispatch,
    selectionsActions,
    formatMessage,
    hidePanel,
    panelFieldId,
    showDialog,
    state,
  ]);

  const shouldShowClearCells = useMemo(() => {
    const database = getDatabaseWithCurrentView(state, { contentId });
    const selectedFieldIds = selectSelectedFieldIds(state, { contentId });

    for (const fieldId of selectedFieldIds) {
      const field = database.fieldsById[fieldId];

      if (
        field.type === DatabaseFieldType.PAGE_STATUS ||
        field.type === DatabaseFieldType.PAGE_LABELS
      ) {
        return field.settings.allowEdit;
      }

      if (DEPENDENCY_FIELD_TYPES.has(field.type)) {
        return false;
      }
    }

    return true;
  }, [contentId, state]);

  return {
    handleDeleteFields,
    shouldShowClearCells,
  };
};
